import { useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React from 'react';

import Button from '@/components/Button';
import InputField from '@/components/InputField';
import { Product } from '@/types/fulfill/inventory';

import DropzoneImage from './DropzoneImage';
import useEditProduct from './useEditProduct';

export interface EditProductProps {
  product: Product;
  getProduct: () => Promise<void>;
  onClose: () => void;
}

const numericFieldProps = {
  type: 'number',
  textFieldProps: {
    inputProps: {
      step: '1',
      min: '0',
      pattern: '[0-9]*',
    },
    onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
      }
    },
    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedData = e.clipboardData.getData('text');
      if (!/^\d+$/.test(pastedData)) {
        e.preventDefault();
      }
    },
  },
};

const EditProduct = ({ product, getProduct, onClose }: EditProductProps) => {
  const theme = useTheme();
  const {
    errors,
    register,
    handleSubmit,
    onSubmit,
    onChangeImage,
    resetImage,
    isDirty,
    loading,
    newImage,
  } = useEditProduct({
    product,
    getProduct,
    onClose,
  });

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          pt: 1,
          px: 2,
          pb: 2,
          gap: 2,
          mt: 2,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <InputField
            label="Título"
            name="name"
            register={register}
            errors={errors}
            sxInput={{ mt: 0 }}
            sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
            placeholder={product?.name}
          />
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <InputField
              label="Costo de venta"
              name="price"
              register={register}
              errors={errors}
              placeholder={product?.price?.toString()}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              {...numericFieldProps}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <InputField
              label="Costo interno"
              name="value"
              register={register}
              errors={errors}
              placeholder={product?.value?.toString()}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              {...numericFieldProps}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 'bold',
                textAlign: 'start',
              }}
            >
              Fotografía actual
            </Typography>
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <img
                src={product?.thumbnail}
                alt="product"
                style={{
                  width: '95px',
                  height: '95px',
                  borderRadius: '1rem',
                  objectFit: 'cover',
                }}
              />
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DropzoneImage onChange={onChangeImage} onReset={resetImage} currentImage={newImage} />
          </Grid>
        </Grid>
      </Paper>

      <Grid container mt={4} justifyContent="flex-end" alignItems="center" width="100%">
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isDirty || loading}
          sx={{ minWidth: '120px' }}
        >
          {loading ? <CircularProgress size={20} color="primary" /> : 'Confirmar'}
        </Button>
      </Grid>
    </>
  );
};

export default EditProduct;
